import { useEffect, useState, useCallback, useRef } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import {
  Rss, Plus, Play, Trash2, Edit3, Download, TestTube, X, Loader2,
  AlertTriangle, CheckCircle2, Sparkles, Link2, RefreshCcw, ArrowUp, ArrowDown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FEED = {
  name: '', url: '', type: 'rss', category: 'Vijesti', active: true,
  filter_terms: [], skip_terms: [],
  selector_article: null, selector_separator: null, county: null,
  override_author: false, import_status: 'draft',
  image_llm_provider: 'none', image_llm_model: null, source_url_display: 'full',
  auto_crawl_enabled: false, auto_crawl_interval_hours: 4,
  peak_hour_start: null, peak_hour_end: null, peak_interval_hours: null,
  ai_processing: false, ai_prompt: null,
  ai_model_provider: null, ai_model_name: null,
  content_strip_selectors: null, content_strip_terms: null,
  import_ai_prompt: null, import_ai_model_provider: null, import_ai_model_name: null,
  excerpt_only: null, full_text_service_base_url: 'https://ftr.fivefilters.org/',
  similarity_threshold: 0.85,
  source_homepage_url: '',
  max_entries: 500,
  max_age_days: 0,
};

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs text-zinc-400 block mb-1">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-zinc-600 mt-0.5 block">{hint}</span>}
    </label>
  );
}

const inp = "w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 focus:border-red-500 outline-none";

function FeedFormModal({ feed, providers, authFetch, onSave, onClose }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_FEED, ...feed }));
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setList = (k, s) => set(k, (s || '').split(',').map(x => x.trim()).filter(Boolean));

  // Load existing site categories so the feed admin can pick from a dropdown
  // instead of typing free-form text. Selecting "(unesi novu)" still allows
  // typing a custom value via the inline input below.
  useEffect(() => {
    fetch(`${API}/public/categories?lang=hr`).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.categories) setCategories(d.categories);
    }).catch(() => {});
  }, []);

  async function save() {
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="feed-form-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <header className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
          <h2 className="font-serif text-lg text-zinc-100">{feed?.id ? 'Uredi feed' : 'Novi RSS/Web feed'}</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100"><X size={18} /></button>
        </header>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <Field label="Naziv*"><input className={inp} value={form.name} onChange={e => set('name', e.target.value)} data-testid="feed-name" /></Field>
          <Field label="URL*"><input className={inp} value={form.url} onChange={e => set('url', e.target.value)} data-testid="feed-url" /></Field>
          <Field label="Tip">
            <select className={inp} value={form.type} onChange={e => set('type', e.target.value)} data-testid="feed-type">
              <option value="rss">RSS</option><option value="web">Web scrape</option>
            </select>
          </Field>
          <Field label="Kategorija (zadana)" hint="Odaberi iz postojećih kategorija ili ručno upiši novu.">
            <select className={inp} value={categories.some(c => c.name_hr === form.category || c.slug === form.category) ? form.category : '__custom__'}
                    onChange={e => { if (e.target.value !== '__custom__') set('category', e.target.value); }}
                    data-testid="feed-category-select">
              {categories.map(c => (
                <option key={c.id} value={c.name_hr}>{c.name_hr}</option>
              ))}
              <option value="__custom__">— ručni unos —</option>
            </select>
            <input className={inp + ' mt-1'} value={form.category}
                   onChange={e => set('category', e.target.value)}
                   placeholder="Ručno upiši kategoriju ako želiš"
                   data-testid="feed-category-input" />
          </Field>
          <Field label="Status uvezenih članaka">
            <select className={inp} value={form.import_status} onChange={e => set('import_status', e.target.value)}>
              <option value="draft">Nacrt</option><option value="published">Objavljeno</option>
            </select>
          </Field>
          <Field label="Prikaz izvora">
            <select className={inp} value={form.source_url_display} onChange={e => set('source_url_display', e.target.value)}>
              <option value="full">Puni URL</option><option value="domain">Samo domena</option>
            </select>
          </Field>
          <Field label="Filter termini (whitelist — samo ako sadrži)" hint="zarez-odvojeno">
            <input className={inp} value={(form.filter_terms || []).join(', ')} onChange={e => setList('filter_terms', e.target.value)} data-testid="feed-filter-terms" />
          </Field>
          <Field label="Skip termini (blacklist — preskoči ako sadrži)" hint="zarez-odvojeno">
            <input className={inp} value={(form.skip_terms || []).join(', ')} onChange={e => setList('skip_terms', e.target.value)} data-testid="feed-skip-terms" />
          </Field>
          {form.type === 'web' && <>
            <Field label="CSS selector članka"><input className={inp} value={form.selector_article || ''} onChange={e => set('selector_article', e.target.value)} /></Field>
            <Field label="CSS selector naslova"><input className={inp} value={form.selector_separator || ''} onChange={e => set('selector_separator', e.target.value)} /></Field>
          </>}
          <Field label="Županija"><input className={inp} value={form.county || ''} onChange={e => set('county', e.target.value)} /></Field>
          <Field label="Kanonski 'Izvor' link (URL županije)" hint="Kad je postavljeno, svaki uvezeni članak koristi ovaj URL kao 'Izvor' umjesto direktnog RSS linka.">
            <input className={inp} type="url" placeholder="npr. https://varazdinska-zupanija.hr/"
                   value={form.source_homepage_url || ''}
                   onChange={e => set('source_homepage_url', e.target.value)}
                   data-testid="feed-source-homepage-url" />
          </Field>
          <Field label="Full-text ekstrakcija (base URL)"><input className={inp} value={form.full_text_service_base_url || ''} onChange={e => set('full_text_service_base_url', e.target.value)} /></Field>
          <Field label="Prag sličnosti (0.00–1.00)" hint="Dupli naslovi iznad ovog praga preskaču se. 1.00 = isključi fuzzy dedup.">
            <input type="number" step="0.01" min="0" max="1" className={inp}
                   value={form.similarity_threshold ?? 0.85}
                   onChange={e => set('similarity_threshold', Math.max(0, Math.min(1, parseFloat(e.target.value) || 0.85)))}
                   data-testid="feed-similarity-threshold" />
          </Field>
          <Field label="Maks. unosa po crawlu" hint="Koliko članaka maksimalno povući iz feeda po crawlu (10–2000, default 500). Postavi veliki broj da se uveze sve.">
            <input type="number" min="10" max="2000" step="10" className={inp}
                   value={form.max_entries ?? 500}
                   onChange={e => set('max_entries', Math.max(10, Math.min(2000, parseInt(e.target.value) || 500)))}
                   data-testid="feed-max-entries" />
          </Field>
          <Field label="Maks. starost (dani, 0 = bez limita)" hint="Ne uvozi članke starije od X dana. Preporuke: 1, 7, 30.">
            <select className={inp} value={form.max_age_days ?? 0}
              onChange={e => set('max_age_days', parseInt(e.target.value) || 0)}
              data-testid="feed-max-age-days">
              <option value={0}>Bez limita</option>
              <option value={1}>1 dan</option>
              <option value={3}>3 dana</option>
              <option value={7}>7 dana (1 tjedan)</option>
              <option value={14}>14 dana</option>
              <option value={30}>30 dana (1 mjesec)</option>
              <option value={90}>90 dana</option>
              <option value={365}>365 dana</option>
            </select>
          </Field>
          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} data-testid="feed-active" /> Aktivan
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={!!form.override_author} onChange={e => set('override_author', e.target.checked)} /> Override autora
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={!!form.ai_processing} onChange={e => set('ai_processing', e.target.checked)} data-testid="feed-ai-processing" /> AI obrada
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={!!form.auto_crawl_enabled} onChange={e => set('auto_crawl_enabled', e.target.checked)} /> Automatski crawl
            </label>
          </div>
          {form.auto_crawl_enabled && <>
            <Field label="Interval (sati)"><input type="number" className={inp} value={form.auto_crawl_interval_hours} onChange={e => set('auto_crawl_interval_hours', +e.target.value)} /></Field>
            <Field label="Peak start (sat)"><input type="number" className={inp} value={form.peak_hour_start ?? ''} onChange={e => set('peak_hour_start', e.target.value ? +e.target.value : null)} /></Field>
            <Field label="Peak end (sat)"><input type="number" className={inp} value={form.peak_hour_end ?? ''} onChange={e => set('peak_hour_end', e.target.value ? +e.target.value : null)} /></Field>
            <Field label="Peak interval (sati)"><input type="number" className={inp} value={form.peak_interval_hours ?? ''} onChange={e => set('peak_interval_hours', e.target.value ? +e.target.value : null)} /></Field>
          </>}
          {form.ai_processing && <>
            <Field label="AI provajder/model">
              <select className={inp} value={`${form.ai_model_provider || ''}/${form.ai_model_name || ''}`}
                      onChange={e => { const [p, m] = e.target.value.split('/'); set('ai_model_provider', p || null); set('ai_model_name', m || null); }}>
                <option value="/">Globalno (default)</option>
                {providers.text.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Image LLM">
              <select className={inp} value={form.image_llm_provider} onChange={e => set('image_llm_provider', e.target.value)}>
                <option value="none">Nema</option>
                {providers.image.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </Field>
            <div className="md:col-span-2">
              <Field label="AI prompt (override global)">
                <textarea rows={4} className={inp} value={form.ai_prompt || ''} onChange={e => set('ai_prompt', e.target.value || null)} />
              </Field>
            </div>
          </>}
          <Field label="Strip CSS selektori" hint="zarez-odvojeno, za uklanjanje iz sadržaja">
            <input className={inp} value={form.content_strip_selectors || ''} onChange={e => set('content_strip_selectors', e.target.value || null)} />
          </Field>
          <Field label="Strip termini (tekst za uklanjanje)" hint="zarez-odvojeno">
            <input className={inp} value={form.content_strip_terms || ''} onChange={e => set('content_strip_terms', e.target.value || null)} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={!!form.excerpt_only} onChange={e => set('excerpt_only', e.target.checked)} />
            Feed daje samo sažetke (koristi FiveFilters)
          </label>
          {form.url && (
            <div className="md:col-span-2">
              <button
                type="button"
                onClick={async () => {
                  const tid = toast.loading('Testiram ekstrakciju…');
                  try {
                    const r = await authFetch(`${API}/admin/feeds/test-fulltext-expansion`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ url: form.url, service_base_url: form.full_text_service_base_url || 'https://ftr.fivefilters.org/' }),
                    });
                    const d = await r.json();
                    toast.success(d.success ? `Ekstrakcija OK (${d.content_length} znakova)` : 'Ekstrakcija nije uspjela', { id: tid });
                    window.alert(`Duljina sadržaja: ${d.content_length}\nSlika: ${d.image || '—'}\n\nPredpregled:\n${d.content_preview || '(prazno)'}`);
                  } catch (e) { toast.error('Greška', { id: tid }); }
                }}
                className="px-4 py-2 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 rounded-md flex items-center gap-1.5"
                data-testid="test-fulltext-btn"
              >
                <TestTube size={12} /> Testiraj Full-Text ekstrakciju (URL feeda)
              </button>
            </div>
          )}
        </div>
        <footer className="px-6 py-4 border-t border-zinc-800 flex justify-end gap-3 sticky bottom-0 bg-zinc-950">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200">Odustani</button>
          <button onClick={save} disabled={saving || !form.name || !form.url}
                  className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center gap-2 disabled:opacity-50" data-testid="feed-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}Spremi
          </button>
        </footer>
      </div>
    </div>
  );
}

function CrawlStatusBar({ jobId, authFetch, onDone }) {
  const [job, setJob] = useState(null);
  const authRef = useRef(authFetch);
  const doneRef = useRef(onDone);
  useEffect(() => { authRef.current = authFetch; doneRef.current = onDone; });
  useEffect(() => {
    let stop = false;
    let timer = null;
    const poll = async () => {
      if (stop) return;
      try {
        const r = await authRef.current(`${API}/admin/feeds/crawl-status/${jobId}`);
        const d = await r.json();
        if (stop) return;
        setJob(d);
        if (d.status === 'completed' || d.status === 'error') {
          doneRef.current?.(d);
          return;
        }
      } catch {
        /* keep polling */
      }
      if (!stop) timer = setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [jobId]);
  if (!job) return <span className="text-xs text-zinc-500 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> pokrećem…</span>;
  const pct = job.total_entries ? Math.round(job.processed / job.total_entries * 100) : 0;
  if (job.status === 'error') return <span className="text-xs text-red-400">{job.error_message}</span>;
  return (
    <div className="text-xs" data-testid={`crawl-status-${jobId.slice(0, 6)}`}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span>{job.status === 'completed' ? <CheckCircle2 size={12} className="text-emerald-400 inline" /> : <Loader2 size={12} className="animate-spin inline text-amber-400" />}</span>
        <span>{job.processed}/{job.total_entries} — {pct}%</span>
        <span className="text-emerald-400">+{job.imported}</span>
        <span className="text-zinc-500">dup:{job.duplicates}</span>
        <span className="text-zinc-500">skip:{job.skipped}</span>
        {job.replayed ? <span className="text-sky-400">replay:{job.replayed}</span> : null}
        {job.errors ? <span className="text-red-400">err:{job.errors}</span> : null}
        {job.include_skipped ? <span className="text-[10px] px-1.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30">ponovni pokušaj preskočenih</span> : null}
      </div>
      <div className="h-1 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-red-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function AdminFeedsV3() {
  const { authFetch } = useAdmin();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState({ text: [], image: [] });
  const [editing, setEditing] = useState(null);
  const [jobs, setJobs] = useState({});
  const [includeSkipped, setIncludeSkipped] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        authFetch(`${API}/admin/feeds`),
        authFetch(`${API}/admin/feeds/llm-providers`),
      ]);
      setFeeds((await r1.json()).feeds || []);
      setProviders(await r2.json());
    } finally { if (!silent) setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const handleCrawlDone = useCallback(() => { load(true); }, [load]);

  async function saveFeed(form) {
    const method = form.id ? 'PUT' : 'POST';
    const url = form.id ? `${API}/admin/feeds/${form.id}` : `${API}/admin/feeds`;
    const r = await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (r.ok) { toast.success('Feed spremljen'); load(true); }
    else toast.error('Greška pri spremanju');
  }

  async function deleteFeed(id) {
    if (!window.confirm('Obriši feed?')) return;
    await authFetch(`${API}/admin/feeds/${id}`, { method: 'DELETE' });
    load(true);
  }

  async function crawlNow(id) {
    const qs = includeSkipped ? '?include_skipped=true' : '';
    const r = await authFetch(`${API}/admin/feeds/${id}/crawl${qs}`, { method: 'POST' });
    const d = await r.json();
    setJobs(j => ({ ...j, [id]: d.job_id }));
    toast.info(includeSkipped ? 'Crawl pokrenut (uključujući preskočene)' : 'Crawl pokrenut');
  }

  async function ingestAll() {
    const qs = includeSkipped ? '?include_skipped=true' : '';
    const r = await authFetch(`${API}/admin/feeds/ingest-all${qs}`, { method: 'POST' });
    const d = await r.json();
    toast.success(`${d.count} feedova pokrenuto${includeSkipped ? ' (uključujući preskočene)' : ''}`);
    const newJobs = {};
    for (const j of d.jobs) { if (j.feed_id) newJobs[j.feed_id] = j.job_id; }
    setJobs(x => ({ ...x, ...newJobs }));
  }

  async function analyzeFeed(id) {
    const r = await authFetch(`${API}/admin/feeds/${id}/analyze`, { method: 'POST' });
    const d = await r.json();
    if (!d.success) { toast.error(d.error); return; }
    const msg = `Naslov: ${d.feed_title || '-'}\nUnosa: ${d.entries_count}\nSažeci-only: ${d.excerpt_only_detected ? 'DA' : 'NE'}\n\nPreporuke:\n${(d.recommendations || []).join('\n') || '(nema)'}`;
    window.alert(msg);
  }

  if (loading) return <div className="p-10 text-center text-zinc-500"><Loader2 className="inline animate-spin mr-2" /> Učitavanje…</div>;

  return (
    <div className="p-6 max-w-7xl" data-testid="admin-feeds-v3">
      <header className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3"><Rss size={28} className="text-red-500" /> RSS / Web feedovi</h1>
          <p className="text-xs text-zinc-500 mt-1">Upravljanje izvorima, AI obrada, FiveFilters, duplikati i auto-crawl.</p>
        </div>
        <div className="flex gap-3 items-center">
          <label className="flex items-center gap-2 text-xs text-zinc-300 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2"
                 title="Kad je uključeno, crawl će ponovno pokušati uvesti članke koji su prije preskočeni (ali još nisu objavljeni ili odbačeni).">
            <input type="checkbox" checked={includeSkipped} onChange={e => setIncludeSkipped(e.target.checked)}
                   data-testid="include-skipped-toggle" />
            <RefreshCcw size={13} /> Ponovno uvezi preskočene članke
          </label>
          <button onClick={ingestAll} className="px-4 py-2 bg-zinc-900 border border-zinc-800 hover:border-red-500 text-zinc-300 text-sm rounded-md flex items-center gap-2" data-testid="ingest-all-btn">
            <Play size={14} /> Uvezi sve aktivne
          </button>
          <button onClick={() => setEditing({})} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md flex items-center gap-2" data-testid="new-feed-btn">
            <Plus size={14} /> Novi feed
          </button>
        </div>
      </header>

      <div className="space-y-2" data-testid="feeds-list">
        {feeds.length === 0 && <div className="text-center py-10 text-zinc-500">Nema feedova.</div>}
        {feeds.map(f => (
          <div key={f.id} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4" data-testid={`feed-row-${f.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-zinc-100">{f.name}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${f.active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>
                    {f.active ? 'AKTIVAN' : 'NEAKTIVAN'}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 uppercase">{f.type}</span>
                  {f.ai_processing && <span className="text-[10px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 flex items-center gap-0.5"><Sparkles size={9} /> AI</span>}
                  {f.auto_crawl_enabled && <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">AUTO {f.auto_crawl_interval_hours}h</span>}
                  {f.excerpt_only && <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">EXCERPT-ONLY</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">max: {f.max_entries ?? 500}</span>
                </div>
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-xs text-zinc-500 hover:text-red-400 truncate mt-1 flex items-center gap-1">
                  <Link2 size={10} /> {f.url}
                </a>
                <div className="flex gap-4 text-[11px] text-zinc-500 mt-2">
                  <span>Kategorija: <b className="text-zinc-300">{f.category}</b></span>
                  <span>Uvezeno: <b className="text-zinc-300">{f.articles_imported || 0}</b></span>
                  <span>Zadnji crawl: <b className="text-zinc-300">{f.last_crawled ? new Date(f.last_crawled).toLocaleString('hr') : '—'}</b></span>
                </div>
                {jobs[f.id] && <div className="mt-3"><CrawlStatusBar jobId={jobs[f.id]} authFetch={authFetch} onDone={handleCrawlDone} /></div>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => crawlNow(f.id)} className="px-3 py-1 text-xs bg-red-600/80 hover:bg-red-600 text-white rounded flex items-center gap-1" data-testid={`crawl-btn-${f.id}`}>
                  <Download size={11} /> Crawl{includeSkipped ? '+preskočeni' : ''}
                </button>
                <button onClick={() => analyzeFeed(f.id)} className="px-3 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded flex items-center gap-1">
                  <TestTube size={11} /> Analiziraj
                </button>
                <button onClick={() => setEditing(f)} className="px-3 py-1 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded flex items-center gap-1">
                  <Edit3 size={11} /> Uredi
                </button>
                <button onClick={() => deleteFeed(f.id)} className="px-3 py-1 text-xs bg-red-950 hover:bg-red-900 text-red-300 rounded flex items-center gap-1">
                  <Trash2 size={11} /> Obriši
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && <FeedFormModal feed={editing} providers={providers} authFetch={authFetch} onSave={saveFeed} onClose={() => setEditing(null)} />}

      <SkippedItemsPanel authFetch={authFetch} feeds={feeds} />
    </div>
  );
}

function SkippedItemsPanel({ authFetch, feeds }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ skipped: 0, overridden: 0, dismissed: 0, by_reason: [] });
  const [feedFilter, setFeedFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // desc = najnovije prvo
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort_order: sortOrder });
      if (feedFilter) params.set('feed_id', feedFilter);
      const [listR, statsR] = await Promise.all([
        authFetch(`${API}/admin/feeds/skipped-items?${params.toString()}`).then(r => r.json()),
        authFetch(`${API}/admin/feeds/skipped-stats`).then(r => r.json()),
      ]);
      setItems(listR.items || []);
      setStats(statsR || {});
      setSelected(new Set()); // clear selection on reload
    } finally { setLoading(false); }
  }, [authFetch, feedFilter, sortOrder]);

  useEffect(() => { load(); }, [load]);

  const override = async (id, status) => {
    const r = await authFetch(`${API}/admin/feeds/skipped-items/${id}/override`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (r.ok) { toast.success(status === 'published' ? 'Objavljeno' : 'Uvezeno kao draft'); load(); }
    else toast.error('Greška');
  };
  const dismiss = async (id) => {
    await authFetch(`${API}/admin/feeds/skipped-items/${id}/dismiss`, { method: 'POST' });
    load();
  };
  const del = async (id) => {
    if (!window.confirm('Trajno obrisati ovaj zapis iz queuea?')) return;
    await authFetch(`${API}/admin/feeds/skipped-items/${id}`, { method: 'DELETE' });
    load();
  };

  const toggleSelect = (id) => {
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(it => it.id)));
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Trajno obrisati ${ids.length} preskočenih članaka?`)) return;
    const r = await authFetch(`${API}/admin/feeds/skipped-items/bulk-delete`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (r.ok) { const d = await r.json(); toast.success(`Obrisano: ${d.deleted}`); load(); }
    else toast.error('Greška pri brisanju');
  };
  const bulkDismiss = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const r = await authFetch(`${API}/admin/feeds/skipped-items/bulk-dismiss`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (r.ok) { const d = await r.json(); toast.success(`Odbačeno: ${d.dismissed}`); load(); }
    else toast.error('Greška');
  };
  const bulkOverrideDraft = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const r = await authFetch(`${API}/admin/feeds/skipped-items/bulk-override`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (r.ok) { const d = await r.json(); toast.success(`Uvezeno kao draft: ${d.imported}`); load(); }
    else toast.error('Greška');
  };

  // Trajno briše cijeli "preskočeni članci" log — po statusu i eventualnom
  // feed filteru. Podržava: skipped (default), dismissed, all.
  const clearAllLog = async () => {
    const scopeLabel = feedFilter
      ? 'za odabrani RSS izvor'
      : 'za sve RSS izvore';
    if (!window.confirm(
      `Trajno obrisati SVE preskočene članke ${scopeLabel}?\n\n` +
      `Ova akcija se ne može poništiti.`
    )) return;
    const params = new URLSearchParams({ status: 'skipped', confirm: 'true' });
    if (feedFilter) params.set('feed_id', feedFilter);
    const r = await authFetch(`${API}/admin/feeds/skipped-items?${params}`, { method: 'DELETE' });
    if (r.ok) { const d = await r.json(); toast.success(`Log ispražnjen: ${d.deleted} redaka`); load(); }
    else toast.error('Greška pri brisanju');
  };

  const openPreview = async (id) => {
    setPreviewId(id);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const r = await authFetch(`${API}/admin/feeds/skipped-items/${id}/preview`);
      if (r.ok) setPreviewData(await r.json());
      else { toast.error('Pregled nije dostupan'); setPreviewId(null); }
    } catch { toast.error('Greška pri dohvatu pregleda'); setPreviewId(null); }
    finally { setPreviewLoading(false); }
  };
  const closePreview = () => { setPreviewId(null); setPreviewData(null); };

  const allSelected = items.length > 0 && selected.size === items.length;
  const someSelected = selected.size > 0;

  return (
    <div className="mt-8 border-t border-zinc-800 pt-6" data-testid="skipped-items-panel">
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <h2 className="font-serif text-xl text-zinc-100 flex items-center gap-2">
          <AlertTriangle size={20} className="text-amber-400" /> Preskočeni članci
        </h2>
        <span className="text-xs text-amber-400">{stats.skipped || 0} na čekanju</span>
        <span className="text-xs text-emerald-400">{stats.overridden || 0} ručno uvezeno</span>
        <span className="text-xs text-zinc-500">{stats.dismissed || 0} odbačeno</span>
        <select value={feedFilter} onChange={e => setFeedFilter(e.target.value)}
                className="ml-auto bg-zinc-900 border border-zinc-800 text-xs rounded px-2 py-1"
                data-testid="skipped-feed-filter">
          <option value="">Svi feedovi</option>
          {feeds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <button
          onClick={() => setSortOrder(o => o === 'desc' ? 'asc' : 'desc')}
          className="text-xs px-2 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded inline-flex items-center gap-1 text-zinc-300"
          data-testid="skipped-sort-toggle"
          title={sortOrder === 'desc' ? 'Najnovije prvo (klikni za najstarije prvo)' : 'Najstarije prvo (klikni za najnovije prvo)'}
        >
          Datum uvoza {sortOrder === 'desc' ? <ArrowDown size={11} /> : <ArrowUp size={11} />}
        </button>
        <button onClick={load} className="text-xs px-2 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded">
          Osvježi
        </button>
        <button
          onClick={clearAllLog}
          disabled={!(stats.skipped > 0)}
          className="text-xs px-2 py-1 bg-red-900 hover:bg-red-800 border border-red-800 rounded text-red-100 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
          data-testid="skipped-clear-all-btn"
          title={feedFilter ? 'Briše sve preskočene za odabrani RSS izvor' : 'Briše sve preskočene za sve RSS izvore'}
        >
          <Trash2 size={11} /> Isprazni log
        </button>
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        Svaki članak koji RSS pipeline odbaci završava ovdje s objašnjenjem — tako ne nestaju tiho.
        Možete ih odmah objaviti (zaobilazeći sve filtere) ili odbaciti. Označite više redova za grupne akcije.
      </p>

      {stats.by_reason && stats.by_reason.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5 text-[11px]">
          {stats.by_reason.map(r => (
            <span key={r.reason} className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400">
              <b className="text-zinc-200">{r.count}</b> × {r.reason}
            </span>
          ))}
        </div>
      )}

      {/* Bulk action toolbar */}
      {items.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-3 bg-zinc-900/40 border border-zinc-800 rounded-md px-3 py-2"
             data-testid="skipped-bulk-toolbar">
          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                   ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                   data-testid="skipped-select-all" />
            Odaberi sve ({items.length})
          </label>
          <span className="text-xs text-zinc-500">
            Odabrano: <b className="text-zinc-200">{selected.size}</b>
          </span>
          <div className="ml-auto flex gap-2">
            <button onClick={bulkOverrideDraft} disabled={!someSelected}
                    data-testid="skipped-bulk-override-btn"
                    className="text-[11px] px-3 py-1 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded">
              Uvezi odabrane (draft)
            </button>
            <button onClick={bulkDismiss} disabled={!someSelected}
                    data-testid="skipped-bulk-dismiss-btn"
                    className="text-[11px] px-3 py-1 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 disabled:opacity-40 rounded">
              Odbaci odabrane
            </button>
            <button onClick={bulkDelete} disabled={!someSelected}
                    data-testid="skipped-bulk-delete-btn"
                    className="text-[11px] px-3 py-1 bg-red-900 hover:bg-red-800 text-red-100 disabled:opacity-40 rounded flex items-center gap-1">
              <Trash2 size={11} /> Obriši odabrane
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-zinc-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-1" /> Učitavanje…
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-800 rounded">
          Nema preskočenih članaka.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map(it => (
            <div key={it.id} className={`bg-zinc-900 border rounded p-3 ${selected.has(it.id) ? 'border-sky-600' : 'border-zinc-800'}`}
                 data-testid={`skipped-row-${it.id}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelect(it.id)}
                       className="mt-1.5 shrink-0"
                       data-testid={`skipped-select-${it.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase font-bold text-orange-400">{it.feed_name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                      {it.reason}
                    </span>
                    <span className="text-[11px] text-zinc-400">{it.reason_label}</span>
                    {Array.isArray(it?.extra?.matched_skip_terms) && it.extra.matched_skip_terms.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30" data-testid={`skipped-matched-${it.id}`}>
                        Trigger riječ: &quot;{it.extra.matched_skip_terms.join(', ')}&quot;
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm text-zinc-100 mt-0.5 truncate">{it.title || '(bez naslova)'}</h4>
                  {it.link && (
                    <a href={it.link} target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-blue-400 hover:underline truncate block">
                      {it.link}
                    </a>
                  )}
                  <span className="text-[10px] text-zinc-600">{new Date(it.created_at).toLocaleString('hr-HR')}</span>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => setExpanded(e => e === it.id ? null : it.id)}
                          className="text-[11px] px-2 py-0.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded">
                    {expanded === it.id ? 'Sakrij' : 'Detalji'}
                  </button>
                  <button onClick={() => openPreview(it.id)}
                          data-testid={`skipped-preview-${it.id}`}
                          className="text-[11px] px-2 py-0.5 bg-sky-600 hover:bg-sky-700 text-white rounded">
                    Pregled
                  </button>
                  <button onClick={() => override(it.id, 'published')}
                          data-testid={`skipped-publish-${it.id}`}
                          className="text-[11px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded">
                    Objavi
                  </button>
                  <button onClick={() => override(it.id, 'draft')}
                          data-testid={`skipped-draft-${it.id}`}
                          className="text-[11px] px-2 py-0.5 bg-amber-600 hover:bg-amber-700 text-white rounded">
                    Uvezi (draft)
                  </button>
                  <button onClick={() => dismiss(it.id)}
                          data-testid={`skipped-dismiss-${it.id}`}
                          className="text-[11px] px-2 py-0.5 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 rounded">
                    Odbaci
                  </button>
                  <button onClick={() => del(it.id)}
                          data-testid={`skipped-delete-${it.id}`}
                          className="text-[11px] px-2 py-0.5 bg-red-900 hover:bg-red-800 text-red-200 rounded flex items-center gap-1">
                    <Trash2 size={10} /> Obriši
                  </button>
                </div>
              </div>
              {expanded === it.id && (
                <div className="mt-2 pt-2 border-t border-zinc-800 text-[11px] space-y-1">
                  {it.extra && Object.keys(it.extra).length > 0 && (
                    <pre className="bg-zinc-950 p-2 rounded text-zinc-400 overflow-auto max-h-32">
                      {JSON.stringify(it.extra, null, 2)}
                    </pre>
                  )}
                  {it.entry_snapshot?.summary && (
                    <p className="text-zinc-400 italic max-h-24 overflow-auto">{it.entry_snapshot.summary}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {previewId && (
        <SkippedPreviewModal
          data={previewData}
          loading={previewLoading}
          onClose={closePreview}
          onPublish={async () => { await override(previewId, 'published'); closePreview(); }}
          onDraft={async () => { await override(previewId, 'draft'); closePreview(); }}
          onDismiss={async () => { await dismiss(previewId); closePreview(); }}
        />
      )}
    </div>
  );
}

function SkippedPreviewModal({ data, loading, onClose, onPublish, onDraft, onDismiss }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start justify-center pt-10 pb-10 px-4 overflow-y-auto"
         data-testid="skipped-preview-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-w-3xl w-full shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="font-serif text-lg text-zinc-100">Pregled preskočenog članka</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="skipped-preview-close">
            <X size={18} />
          </button>
        </div>
        {loading && (
          <div className="p-8 text-center text-zinc-500 text-sm">
            <Loader2 size={16} className="inline animate-spin mr-2" /> Dohvaćam i ponovno ekstrahiram članak…
          </div>
        )}
        {!loading && data && (
          <div className="p-5 space-y-4">
            <div>
              <div className="text-[10px] uppercase text-orange-400 mb-1">{data.feed_name}</div>
              <h2 className="text-xl font-semibold text-zinc-100">{data.title}</h2>
              {data.link && (
                <a href={data.link} target="_blank" rel="noopener noreferrer"
                   className="text-[11px] text-blue-400 hover:underline">{data.link}</a>
              )}
            </div>

            <div className="flex flex-wrap gap-2 text-[11px]">
              <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                Razlog: {data.reason}
              </span>
              {Array.isArray(data.matched_skip_terms) && data.matched_skip_terms.length > 0 && (
                <span className="px-2 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">
                  Originalni trigger: &quot;{data.matched_skip_terms.join(', ')}&quot;
                </span>
              )}
              {Array.isArray(data.live_matches) && data.live_matches.length > 0 ? (
                <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-200 border border-rose-500/40"
                      data-testid="skipped-preview-live-match">
                  I dalje se podudara s: &quot;{data.live_matches.join(', ')}&quot;
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      data-testid="skipped-preview-no-live-match">
                  Čisto tijelo · nema više skip termina
                </span>
              )}
              <span className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400">
                {data.body_length || 0} znakova
              </span>
              {Array.isArray(data.gallery) && (
                <span className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-400">
                  {data.gallery.length} fotografija
                </span>
              )}
            </div>

            {data.excerpt && (
              <p className="text-sm italic text-zinc-400 border-l-2 border-zinc-700 pl-3">{data.excerpt}</p>
            )}

            <div className="border-t border-zinc-800 pt-3">
              <h4 className="text-xs uppercase text-zinc-500 mb-2">Očišćeno tijelo (pregled)</h4>
              <div className="prose prose-invert prose-sm max-w-none max-h-[50vh] overflow-y-auto bg-zinc-900 rounded p-4 text-zinc-200"
                   data-testid="skipped-preview-body"
                   dangerouslySetInnerHTML={{ __html: data.body_html || '<p class="text-zinc-500 italic">Tijelo nije moguće ekstrahirati.</p>' }} />
            </div>

            {Array.isArray(data.gallery) && data.gallery.length > 0 && (
              <div className="border-t border-zinc-800 pt-3">
                <h4 className="text-xs uppercase text-zinc-500 mb-2">Galerija ({data.gallery.length})</h4>
                <div className="grid grid-cols-4 gap-2">
                  {data.gallery.slice(0, 12).map((g, i) => (
                    <img key={i} src={g} alt="" className="w-full h-20 object-cover rounded border border-zinc-800"
                         loading="lazy" />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-zinc-800 bg-zinc-950 sticky bottom-0">
          <button onClick={onDismiss}
                  className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 rounded"
                  data-testid="skipped-preview-dismiss-btn">
            Odbaci
          </button>
          <button onClick={onDraft}
                  className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded"
                  data-testid="skipped-preview-draft-btn">
            Uvezi kao draft
          </button>
          <button onClick={onPublish}
                  className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded"
                  data-testid="skipped-preview-publish-btn">
            Objavi odmah
          </button>
        </div>
      </div>
    </div>
  );
}
